import React from 'react';
import {FixParamIsGroup} from '../utils';
import {Button, Grid} from '@mui/material';
import { withStyles } from '@mui/styles';
import OutlinedSelect from '../OutlinedSelect';
import PropTypes from 'prop-types';
import CompactTextField from '../CompactTextField';
import { cloneDeep } from 'lodash';

const styles = theme => ({
    groupContainer: {
        maxWidth: '100%',
        border: theme.border,
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
        marginBottom: '12px !important',
    },
    inputContainer: {
        maxWidth: '100%',
        marginBottom: 8,
    },
    messageSelect: {
        maxWidth: '100%',
    },
    sendButtons: {
        borderTop: theme.border,
        paddingTop: 6,
    }
});

const INSTRUMENT_DROPDOWN_FIELDS = ['55', '311', '600'];

class FixRequestBuilder extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            selectedMessage: undefined,
        };

        this.messageChanged = this.messageChanged.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    send() {
        const parts = this.getFormValues(this.state.selectedMessage.parameters);
        const message = {
            type: this.state.selectedMessage.messageType,
            name: this.state.selectedMessage.name,
            parts,
        };

        this.props.onSend(message, 'FIX');
    }

    onKeyDown(event) {
        if (event.key === 'Enter' && event.ctrlKey) {
            this.send();
        }
    }

    messageChanged(messageName) {
        if (!messageName) {
            return this.setState({ selectedMessage: undefined });
        }

        const selectedMessage = cloneDeep(this.props.fixSpecs.messages.find(message => message.name === messageName));
        this.setState({ selectedMessage });
    }

    getInstruments() {
        return this.props.instruments;
    }

    setFormValue(parameter, value) {
        parameter.value = value;
        this.setState({ selectedMessage: { ...this.state.selectedMessage }} );
    }

    getFormValues(parameters) {
        return parameters.map(param => {
            const paramObj = param.value && { tag: Number(param.number.split('_')[0]), value: param.value };

            return FixParamIsGroup(param)
                ? [paramObj, ...this.getFormValues(param.parameters)]
                : paramObj;
        })
            .reduce((a, b) => a.concat(b), [])
            .filter(p => !!p);
    }

    setGroupParam(group, value) {
        const newParams = [];
        const originalParams = group.parameters.filter(p => !p.dynamic);

        for (let i = 1; i < value; i++) {
            newParams.push(...this.addParameters(originalParams, i));
        }

        // always keep the original parameters
        group.parameters = [...originalParams, ...newParams];
        group.value = value;

        this.setState({ selectedMessage: { ...this.state.selectedMessage }});
    }

    addParameters(parameters, index, dynamic = true) {
        return parameters.filter(p => !p.dynamic).map((parameter, i) => FixParamIsGroup(parameter)
            ? {
                ...parameter,
                number: `${parameter.number}_${index}`,
                dynamic,
                parameters: this.addParameters(parameter.parameters, `${index + 1}_${i}`, false),
            }
            : {
                ...parameter,
                number: `${parameter.number}_${index}`,
                dynamic,
            });
    }

    renderParameters(message) {
        const parameters = message?.parameters;
        if (!parameters) {
            return;
        }

        return (parameters.map((parameter) =>
                FixParamIsGroup(parameter)
                    ? <Grid item className={this.props.classes.groupContainer} key={`${message.name}:${parameter.number}`}>
                        {this.renderParameterInput(parameter)}
                        {this.renderParameters(parameter)}
                    </Grid>
                    : <Grid item className={this.props.classes.inputContainer} key={`${message.name}:${parameter.number}`}>
                        {this.renderParameterInput(parameter)}
                    </Grid>
            )
        );
    }

    renderParameterInput(parameter) {
        const value = parameter.value || '';

        // add instruments dropdown for symbols (55, 311, 600)
        if (INSTRUMENT_DROPDOWN_FIELDS.includes(parameter.number.split('_')[0])) {
            return this.renderDropdown(parameter, value, this.getInstruments());
        }

        if (parameter.values) {
            return this.renderDropdown(parameter, value, parameter.values.map(v => `${v.value} - ${v.description}`));
        }

        switch (parameter.type) {
            case 'INT':
            case 'SEQNUM':
            case 'UTCTIMESTAMP':
            case 'LENGTH':
                return this.renderNumberInput(parameter, value, {integer: true});
            case 'NUMINGROUP':
                return this.renderNumInGroup(parameter, value);
            case 'QTY':
            case 'FLOAT':
            case 'PRICE':
                return this.renderNumberInput(parameter, value);
            case 'CURRENCY':
            case 'STRING':
            case 'DATA':
                return this.renderStringInput(parameter, value);
            case 'BOOLEAN':
                return this.renderBooleanDropdown(parameter, value);
            case 'CHAR':
            case 'MULTIPLECHARVALUE':
                console.error(`[renderParameterInput] - Parameter "${parameter.name}" should have values`);
                return;
            default:
                console.error(`[renderParameterInput] - Unmatched parameter type "${parameter.type}" for parameter "${parameter.name}"`);
                return;
        }
    }

    renderNumInGroup(parameter, value) {
        return <CompactTextField
            name={parameter.name}
            type="integer"
            value={value}
            onChange={((e) => this.setGroupParam(parameter, e.target.value))}

            label={parameter.name}
            required={parameter.required}

            helperText={parameter.description}
        />;
    }

    renderNumberInput(parameter, value, {integer} = {integer: false}) {
        return <CompactTextField
            name={parameter.name}
            type={integer ? 'integer' : 'number'}
            value={value}
            onChange={((e) => this.setFormValue(parameter, e.target.value))}

            label={parameter.name}
            required={parameter.required}

            helperText={parameter.description}
        />;
    }

    renderBooleanDropdown(parameter, value) {
        let displayVal = '';
        if (value !== undefined) {
            displayVal = `${value}`;
        }

        return <OutlinedSelect
            name={parameter.name}
            value={displayVal}
            onChange={((e) => this.setFormValue(parameter, e === 'Yes' ? 'Y' : 'N'))}
            label={parameter.name}
            required={parameter.required}
            descr={parameter.description}

            items={['Yes', 'No']}
            withNone={!parameter.required}
        />;
    }

    renderDropdown(parameter, value, items) {
        return <OutlinedSelect
            name={parameter.name}
            value={value}
            onChange={(e) => this.setFormValue(parameter, e.split(' ')[0])}

            label={parameter.name}
            required={parameter.required}
            descr={parameter.description}

            items={items}
            withNone={!parameter.required}
        />;
    }

    renderStringInput(parameter, value) {
        return <CompactTextField
            name={parameter.name}
            type="text"
            value={value}
            onChange={((e) => this.setFormValue(parameter, e.target.value))}

            label={parameter.name}
            required={parameter.required}

            helperText={parameter.description}
        />;
    }

    render() {
        const fixApi = this.props.fixSpecs;
        if (!fixApi) {
            return null;
        }
        const selectedMessage = this.state.selectedMessage;
        const messageNames = fixApi?.messages.map(m => m.name);
        const parameters = this.renderParameters(selectedMessage);

        return (
            <Grid container direction="column" justifyContent="center" alignItems="stretch" spacing={2} onKeyDown={this.onKeyDown}>
                <Grid item className={this.props.classes.messageSelect}>
                    <OutlinedSelect
                        name="message"
                        value={selectedMessage?.name || ''}
                        onChange={this.messageChanged}
                        descr={selectedMessage?.description || ''}
                        label="Message"
                        required={true}
                        items={messageNames}
                        withNone={false}
                    />
                </Grid>
                {!!selectedMessage && !!selectedMessage.parameters && (
                    <Grid container item direction="column" justifyContent="flex-start" alignItems="stretch">
                        {parameters}
                    </Grid>
                )}
                {!!selectedMessage &&
                    <Grid container item direction="row" justifyContent="flex-end" alignItems="center" spacing={1} className={this.props.classes.sendButtons}>
                        <Grid item><Button size="small" onClick={() => this.messageChanged()}>Cancel</Button></Grid>
                        <Grid item><Button size="small" variant="contained" color="primary" onClick={() => this.send()}>Send</Button></Grid>
                    </Grid>
                }
            </Grid>
        );
    }
}

FixRequestBuilder.propTypes = {
    onSend: PropTypes.func,
    fixSpecs: PropTypes.object,
    instruments: PropTypes.array,
    onMessage: PropTypes.func,

    theme: PropTypes.object,
    classes: PropTypes.object,
};

export default withStyles(styles, {withTheme: true})(FixRequestBuilder);
